/*
 * /lipo-battery-calculator — page logic. Math: core.js; battery types and the
 * strings come inline (window.__BATT) from the page, which prints the same
 * tables server side.
 *
 * Three questions, one battery (docs/why/battery-calculator.md):
 *   runtime — how long will it run (Peukert, inverter/UPS, duty, cold, health);
 *   soc     — how full is it by its resting voltage (a band on LiFePO4's plateau);
 *   charge  — how long to charge, and to what voltage.
 * The answer of one feeds the next: «how full» → «how long from here» → «charge».
 */
import * as B from './core.js';

const SLUG = 'lipo-battery-calculator';
const W = window.__BATT || {};
B.setData(W.data);
const I = W.i18n || {};
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const tr = (k, rep = {}) => String(I[k] != null ? I[k] : k).replace(/:([a-z_]+)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const LOC = (window.__intlLocale && window.__intlLocale()) || 'en';
const RTL = document.documentElement.getAttribute('dir') === 'rtl';
const nf = {};
function num(x, d = 2) {
    if (!(x === x) || x == null || !isFinite(x)) return '—';
    const key = d;
    if (!nf[key]) { try { nf[key] = new Intl.NumberFormat(LOC, { minimumFractionDigits: d, maximumFractionDigits: d }); } catch (e) { nf[key] = { format: v => v.toFixed(d) }; } }
    return nf[key].format(x);
}
const ltr = s => RTL ? '⁦' + s + '⁩' : s;
// Decimal comma or point, spaces as thousands — people type «12,45».
function parse(v) {
    const s = String(v == null ? '' : v).trim().replace(/\s| /g, '').replace(',', '.');
    if (!s) return NaN;
    const x = Number(s);
    return x === x ? x : NaN;
}
function hm(h) {
    if (!(h > 0) || !isFinite(h)) return '—';
    const total = Math.round(h * 60);
    if (total < 60) return tr('dur_m', { m: total });
    const d = Math.floor(total / 1440), hh = Math.floor((total % 1440) / 60), mm = total % 60;
    if (d > 0) return tr('dur_dh', { d, h: hh });
    return mm ? tr('dur_hm', { h: hh, m: mm }) : tr('dur_h', { h: hh });
}

// ── state ────────────────────────────────────────────────────────────────
const S = {
    mode: 'runtime', type: 'car', cells: 6, cap: 60, unit: 'ah', par: 1,
    crate: NaN, cellmax: NaN,
    loadKind: 'w', load: 100, inv: false, eff: 90, idle: 8, from: 100,
    dod: NaN, duty: 100, temp: 25, soh: 100, peukert: NaN,
    volt: NaN, state: 'rest',
    charger: 6, chFrom: 50, chTo: 100, chTemp: 25,
};
let lastSoc = null;       // the last «how full» answer, to carry into runtime / charge
let lastText = '';

function ahOf() {
    const c = B.chem(S.type);
    if (!(S.cap > 0)) return NaN;
    if (S.unit === 'mah') return S.cap / 1000;
    if (S.unit === 'wh') return S.cap / (c.nominal * S.cells);
    return S.cap;
}

// ── render ───────────────────────────────────────────────────────────────
function stat(label, value, sub, cls) {
    return `<div class="tc-stat${cls ? ' ' + cls : ''}"><div class="tc-stat-l">${esc(label)}</div><div class="tc-stat-v">${value}</div>${sub ? `<div class="tc-stat-s">${esc(sub)}</div>` : ''}</div>`;
}
function msgs(list) {
    $('#bc-msgs').innerHTML = list.map(([k, cls, rep]) => `<p class="tc-msg ${cls || 'warn'}">${esc(tr(k, rep))}</p>`).join('');
}
function setAnswer(label, value, sub) {
    $('#bc-answer-l').textContent = label;
    $('#bc-answer-v').innerHTML = value;
    $('#bc-answer-sub').textContent = sub || '';
    $('#bc-live-l').textContent = label;
    $('#bc-live-v').textContent = $('#bc-answer-v').textContent;
}

function render() {
    const c = B.chem(S.type);
    const ah = ahOf();
    const vp = B.packVoltages(S.type, S.cells);
    const how = [];
    const flagsOut = [];
    $('#bc-to-runtime').hidden = true;
    $('#bc-to-charge').hidden = true;
    let summary = '';

    if (S.mode === 'runtime') {
        const load = S.loadKind === 'w' ? { w: S.load } : { a: S.load };
        const r = B.runtime({
            chem: S.type, cells: S.cells, ah, parallel: S.par, load,
            inverter: S.inv && S.loadKind === 'w' ? { eff: S.eff / 100, idleW: S.idle } : null,
            duty: S.duty / 100, fromSoc: S.from, dod: S.dod === S.dod ? S.dod / 100 : undefined,
            tempC: S.temp, soh: S.soh / 100, peukert: S.peukert,
            cRating: S.type === 'lipo' ? S.crate : NaN, cellMaxA: S.type === 'liion' ? S.cellmax : NaN,
        });
        if (!r) { setAnswer(tr('ans_runtime'), '—', tr('need_runtime')); $('#bc-stats').innerHTML = ''; msgs([]); }
        else {
            const floor = Math.round(r.floorSoc);
            setAnswer(tr('ans_runtime'), esc(hm(r.hours)), tr('ans_runtime_sub', { from: Math.round(r.fromSoc), floor }));
            $('#bc-stats').innerHTML = [
                stat(tr('st_to_empty'), esc(hm(r.hoursFull)), tr('st_to_empty_s')),
                stat(tr('st_current'), ltr(num(r.amps, 2)) + ' ' + esc(tr('u_a')), tr('st_current_s', { c: num(r.cRate, 2) })),
                stat(tr('st_power'), ltr(num(r.battW, 0)) + ' ' + esc(tr('u_w')), r.idleW ? tr('st_power_s', { idle: num(r.idleW, 0) }) : ''),
                stat(tr('st_energy'), ltr(num(r.energyWh, 0)) + ' ' + esc(tr('u_wh')), tr('st_usable', { wh: num(r.usableWh, 0) })),
                r.maxA != null ? stat(tr('st_maxa'), ltr(num(r.maxA, 1)) + ' ' + esc(tr('u_a')), '', r.flags.includes('over_current') ? 'bad' : 'good') : '',
            ].join('');
            how.push(tr('how_load', { w: num(S.loadKind === 'w' ? S.load : S.load * r.vNom, 0), duty: Math.round(r.duty * 100) }));
            if (S.inv && S.loadKind === 'w') how.push(tr('how_inv', { eff: Math.round(r.eff * 100), idle: num(r.idleW, 0), bw: num(r.battW, 1) }));
            how.push(tr('how_amps', { bw: num(r.battW, 1), v: num(r.vNom, 1), a: num(r.amps, 2) }));
            how.push(tr('how_cap', { ah: num(ah * S.par, 2), soh: Math.round(r.soh * 100), tf: Math.round(r.tempFactor * 100), t: num(S.temp, 0) }));
            if (r.peukertFactor < 0.999) how.push(tr('how_peukert', { k: num(r.peukert, 2), f: Math.round(r.peukertFactor * 100), rh: c.ratedHours }));
            how.push(tr('how_depth', { from: Math.round(r.fromSoc), floor }));
            how.push(tr('how_result', { h: hm(r.hours) }));
            for (const f of r.flags) flagsOut.push(['f_' + f, f === 'over_current' || f === 'below_floor' ? 'bad' : 'warn', { floor }]);
            summary = tr('copy_runtime', { type: tr('type_' + S.type), v: num(r.vNom, 1), ah: num(ah * S.par, 1), w: num(r.battW, 0), h: hm(r.hours) });
            $('#bc-to-charge').hidden = false;
        }
    } else if (S.mode === 'soc') {
        const r = S.volt > 0 ? B.socFromVoltage(S.type, S.volt, S.cells, { state: S.state }) : null;
        lastSoc = null;
        if (!r) { setAnswer(tr('ans_soc'), '—', tr('need_soc', { full: num(vp.full, 2) })); $('#bc-stats').innerHTML = ''; }
        else {
            const val = r.band ? `${Math.round(r.lo)}–${Math.round(r.hi)}<span class="u"> %</span>`
                : (r.below ? '&lt; ' : '') + Math.round(r.soc) + '<span class="u"> %</span>';
            setAnswer(tr('ans_soc'), val, tr(r.band ? 'ans_soc_band' : 'ans_soc_sub', { v: num(r.perCell, 3) }));
            lastSoc = r.band ? r.lo : r.soc;
            const bottom = c.ocv[0];
            $('#bc-stats').innerHTML = [
                stat(tr('st_full'), ltr(num(vp.full, 2)) + ' ' + esc(tr('u_v')), tr('st_full_s')),
                stat(tr('st_floor'), ltr(num(B.cellAtSoc(S.type, Math.max(bottom[1], 100 - c.dod * 100)) * S.cells, 2)) + ' ' + esc(tr('u_v')), tr('st_floor_s', { p: Math.round(100 - c.dod * 100) })),
                stat(tr('st_low', { p: bottom[1] }), ltr(num(bottom[0] * S.cells, 2)) + ' ' + esc(tr('u_v')), tr('st_low_s'), 'bad'),
            ].join('');
            how.push(tr('how_soc_cell', { v: num(S.volt, 2), n: S.cells, c: num(r.perCell, 3) }));
            how.push(tr('how_soc_table', { type: tr('type_' + S.type) }));
            if (r.band) how.push(tr('how_soc_band', { mv: c.meterMv }));
            for (const f of r.flags) flagsOut.push(['s_' + f, f === 'wrong_pack' || f === 'charging' ? 'bad' : 'warn', {}]);
            summary = tr('copy_soc', { type: tr('type_' + S.type), v: num(S.volt, 2), p: r.band ? `${Math.round(r.lo)}–${Math.round(r.hi)}` : Math.round(r.soc) });
            $('#bc-to-runtime').hidden = false;
            $('#bc-to-charge').hidden = false;
        }
    } else {
        const r = B.chargeTime({ chem: S.type, ah, parallel: S.par, amps: S.charger, fromSoc: S.chFrom, toSoc: S.chTo, tempC: S.chTemp });
        const cv = B.chargeVoltages(S.type, S.cells, S.chTemp);
        const rng = x => x == null ? '—' : (Math.abs(x[0] - x[1]) < 0.005 ? ltr(num(x[0], 2)) : ltr(num(x[0], 2) + '–' + num(x[1], 2))) + ' ' + esc(tr('u_v'));
        if (!r) { setAnswer(tr('ans_charge'), '—', tr('need_charge')); $('#bc-stats').innerHTML = ''; }
        else {
            setAnswer(tr('ans_charge'), esc(hm(r.hours)), tr('ans_charge_sub', { from: Math.round(S.chFrom), to: Math.round(S.chTo), a: num(S.charger, 1) }));
            $('#bc-stats').innerHTML = [
                stat(tr('st_absorb'), rng(cv.absorb), cv.compensated ? tr('st_comp', { t: num(S.chTemp, 0) }) : ''),
                cv.float ? stat(tr('st_float'), rng(cv.float)) : '',
                cv.storage ? stat(tr('st_storage'), ltr(num(cv.storage, 2)) + ' ' + esc(tr('u_v'))) : '',
                stat(tr('st_crate'), ltr(num(r.cRate, 2)) + ' C', '', r.flags.length ? 'warn' : 'good'),
            ].join('');
            how.push(tr('how_bulk', { h: hm(r.bulkH), eff: Math.round(c.chargeEff * 100), knee: c.family === 'lead' ? 80 : 95 }));
            if (r.tailH > 0) how.push(tr(c.family === 'lead' ? 'how_tail_lead' : 'how_tail_li', { h: hm(r.tailH) }));
            for (const f of r.flags) flagsOut.push(['c_' + f, f === 'li_cold' ? 'bad' : 'warn', {}]);
            summary = tr('copy_charge', { type: tr('type_' + S.type), h: hm(r.hours), a: num(S.charger, 1) });
        }
    }
    msgs(flagsOut);
    $('#bc-how').innerHTML = how.map(x => `<li>${esc(x)}</li>`).join('');
    lastText = summary;
    renderVolts(vp);
    drawChart();
    renderWiring();
    save();
    if (summary) T.done();
}

function renderVolts(vp) {
    const u = ' ' + esc(tr('u_v'));
    const rng = x => x == null ? null : (Array.isArray(x) ? (Math.abs(x[0] - x[1]) < 0.005 ? ltr(num(x[0], 2)) : ltr(num(x[0], 1) + '–' + num(x[1], 1))) : ltr(num(x, 2))) + u;
    $('#bc-volts').innerHTML = [
        stat(tr('v_full'), rng(vp.full), tr('v_full_s'), 'good'),
        stat(tr('v_nominal'), rng(vp.nominal)),
        stat(tr('v_empty'), rng(vp.empty), tr('v_empty_s'), 'bad'),
        stat(tr('v_absorb'), rng(vp.absorb)),
        vp.float ? stat(tr('v_float'), rng(vp.float)) : '',
        vp.storage ? stat(tr('v_storage'), rng(vp.storage)) : '',
    ].join('');
}

// Voltage-vs-charge curve of the chosen type and pack, with the reading on it.
function drawChart() {
    const cv = $('#bc-chart');
    if (!cv || !cv.clientWidth) return;
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth, h = 230;
    cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
    const g = cv.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const cs = getComputedStyle(cv);
    const col = (n, f) => (cs.getPropertyValue(n) || '').trim() || f;
    const text = col('--lg-text-2', '#9aa'), muted = col('--lg-text-3', '#778'), accent = col('--lg-accent', '#38bdf8');
    const grid = document.documentElement.getAttribute('data-theme') === 'light' ? 'rgba(0,0,0,.08)' : 'rgba(255,255,255,.08)';
    const c = B.chem(S.type), n = S.cells, t = c.ocv;
    const vMin = t[0][0] * n, vMax = t[t.length - 1][0] * n;
    const pad = (vMax - vMin) * 0.08;
    const lo = vMin - pad, hi = vMax + pad;
    const L = RTL ? 16 : 56, R = RTL ? 56 : 16, T = 12, Bm = 34;
    const pw = w - L - R, ph = h - T - Bm;
    const X = s => RTL ? L + (1 - s / 100) * pw : L + (s / 100) * pw;
    const Y = v => T + (1 - (v - lo) / (hi - lo)) * ph;
    g.font = '11px system-ui, sans-serif';
    g.fillStyle = muted; g.strokeStyle = grid; g.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
        const v = lo + (hi - lo) * i / 4, y = Y(v);
        g.beginPath(); g.moveTo(L, y); g.lineTo(L + pw, y); g.stroke();
        g.textAlign = RTL ? 'left' : 'right'; g.textBaseline = 'middle';
        g.fillText(num(v, n > 3 ? 1 : 2), RTL ? L + pw + 6 : L - 6, y);
    }
    g.textAlign = 'center'; g.textBaseline = 'top';
    for (const s of [0, 25, 50, 75, 100]) g.fillText(s + '%', X(s), T + ph + 6);
    // LiFePO4's plateau: shade where voltage cannot tell charge apart.
    if (S.type === 'lifepo4') {
        g.fillStyle = 'rgba(245,158,11,.10)';
        g.fillRect(Math.min(X(20), X(90)), T, Math.abs(X(90) - X(20)), ph);
    }
    g.beginPath();
    t.forEach(([v, s], i) => { const x = X(s), y = Y(v * n); if (i) g.lineTo(x, y); else g.moveTo(x, y); });
    g.strokeStyle = accent; g.lineWidth = 2.5; g.lineJoin = 'round'; g.stroke();
    // The reading.
    if (S.mode === 'soc' && S.volt > 0) {
        const r = B.socFromVoltage(S.type, S.volt, n, { state: S.state });
        if (r) {
            const y = Y(Math.min(hi, Math.max(lo, S.volt)));
            g.strokeStyle = 'rgba(239,68,68,.75)'; g.setLineDash([4, 4]);
            g.beginPath(); g.moveTo(L, y); g.lineTo(L + pw, y); g.stroke(); g.setLineDash([]);
            g.fillStyle = '#ef4444';
            g.beginPath(); g.arc(X(r.soc), y, 4.5, 0, Math.PI * 2); g.fill();
            if (r.band) { g.fillStyle = 'rgba(239,68,68,.18)'; g.fillRect(Math.min(X(r.lo), X(r.hi)), y - 6, Math.abs(X(r.hi) - X(r.lo)), 12); }
        }
    }
    g.fillStyle = text; g.textAlign = 'center'; g.textBaseline = 'bottom';
    g.fillText(tr('chart_x'), L + pw / 2, h - 1);
    $('#bc-chart-note').textContent = tr(S.type === 'lifepo4' ? 'chart_note_lfp' : 'chart_note', { type: tr('type_' + S.type) });
}

// Series × parallel: batteries for lead-acid (6 V / 12 V blocks), cells for lithium.
function renderWiring() {
    const c = B.chem(S.type);
    let unitCells = 1;
    if (c.family === 'lead') unitCells = S.cells % 6 === 0 ? 6 : (S.cells % 3 === 0 ? 3 : 1);
    const ser = Math.max(1, Math.round(S.cells / unitCells)), par = Math.max(1, S.par | 0);
    const ser2 = Math.min(ser, 16), par2 = Math.min(par, 6);
    const uw = c.family === 'lead' ? 54 : 30, uh = c.family === 'lead' ? 40 : 52, gx = 14, gy = 20, m = 22;
    const W2 = m * 2 + ser2 * uw + (ser2 - 1) * gx, H2 = m * 2 + par2 * uh + (par2 - 1) * gy;
    let s = `<svg viewBox="0 0 ${W2} ${H2}" width="${W2}" role="img" aria-label="${esc(tr('wiring_aria', { s: ser, p: par }))}" xmlns="http://www.w3.org/2000/svg">`;
    for (let p = 0; p < par2; p++) {
        const y = m + p * (uh + gy);
        for (let i = 0; i < ser2; i++) {
            const x = m + i * (uw + gx);
            s += `<rect x="${x}" y="${y}" width="${uw}" height="${uh}" rx="6" fill="rgba(56,189,248,.10)" stroke="rgba(56,189,248,.6)"/>`;
            s += `<text x="${x + 7}" y="${y + 13}" font-size="11" fill="#ef4444" font-weight="700">+</text><text x="${x + uw - 12}" y="${y + 13}" font-size="12" fill="#64748b" font-weight="700">−</text>`;
            if (i < ser2 - 1) s += `<line x1="${x + uw}" y1="${y + uh / 2}" x2="${x + uw + gx}" y2="${y + uh / 2}" stroke="#38bdf8" stroke-width="2"/>`;
        }
        if (p > 0) {
            s += `<line x1="${m - 8}" y1="${y - gy - uh / 2}" x2="${m - 8}" y2="${y + uh / 2}" stroke="#ef4444" stroke-width="1.5"/>`;
            s += `<line x1="${W2 - m + 8}" y1="${y - gy - uh / 2}" x2="${W2 - m + 8}" y2="${y + uh / 2}" stroke="#64748b" stroke-width="1.5"/>`;
        }
    }
    s += '</svg>';
    $('#bc-wiring').innerHTML = s;
    const unitV = num(c.nominal * unitCells, unitCells === 1 ? 1 : 0);
    $('#bc-wiring-cap').textContent = tr(c.family === 'lead' ? 'wiring_cap_lead' : 'wiring_cap_li',
        { s: ser, p: par, uv: unitV, v: num(c.nominal * S.cells, 1), ah: num(ahOf() * par, 1) })
        + (ser > ser2 || par > par2 ? ' ' + tr('wiring_trim') : '');
}

// ── wiring of the controls ───────────────────────────────────────────────
function setType(id, keepCells) {
    S.type = id;
    $$('.bc-types [data-type]').forEach(b => { const on = b.dataset.type === id; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('[data-systems]').forEach(box => { box.hidden = box.dataset.systems !== id; });
    const c = B.chem(id);
    if (!keepCells) {
        S.cells = c.systems[0];
        $$(`[data-systems="${id}"] [data-cells]`).forEach((b, i) => b.classList.toggle('on', i === 0));
        const own = $(`[data-systems="${id}"] [data-cells-own]`); if (own) own.value = '';
    } else markCells();
    $('#bc-type-hint').textContent = tr('type_hint_' + id);
    $('#bc-par-hint').textContent = tr(c.family === 'lead' ? 'parallel_hint_lead' : 'parallel_hint_li');
    $('#bc-crate-wrap').hidden = id !== 'lipo';
    $('#bc-cellmax-wrap').hidden = id !== 'liion';
    $('#bc-cap-l').textContent = tr(id === 'liion' ? 'cap_label_cell' : 'cap_label');
    $('#bc-state-hint').textContent = tr(c.family === 'lead' ? 'state_hint_lead' : (id === 'lifepo4' ? 'state_hint_lfp' : 'state_hint_li'));
    $('#bc-dod').placeholder = String(Math.round(c.dod * 100));
    $('#bc-peukert').placeholder = String(c.peukert);
}
function markCells() {
    const box = $(`[data-systems="${S.type}"]`);
    let hit = false;
    $$('[data-cells]', box).forEach(b => { const on = +b.dataset.cells === S.cells; b.classList.toggle('on', on); hit = hit || on; });
    const own = $('[data-cells-own]', box); if (own) own.value = hit ? '' : String(S.cells);
}
function setMode(m) {
    S.mode = m;
    $$('.bc-modes [data-mode]').forEach(b => { const on = b.dataset.mode === m; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('[data-pane]').forEach(p => { p.hidden = p.dataset.pane !== m; });
    T.touch('mode_' + m);
}
function bindNum(sel, key) {
    const el = $(sel); if (!el) return;
    el.addEventListener('input', () => { S[key] = parse(el.value); render(); });
}

function boot() {
    load();
    // controls → state
    $$('.bc-modes [data-mode]').forEach(b => b.addEventListener('click', () => { setMode(b.dataset.mode); render(); }));
    $$('.bc-types [data-type]').forEach(b => b.addEventListener('click', () => { setType(b.dataset.type); T.touch('type_' + b.dataset.type); render(); }));
    $$('[data-cells]').forEach(b => b.addEventListener('click', () => { S.cells = +b.dataset.cells; markCells(); render(); }));
    $$('[data-cells-own]').forEach(el => el.addEventListener('input', () => {
        const n = Math.round(parse(el.value));
        if (n >= 1 && n <= 64) { S.cells = n; $$('[data-cells]', el.closest('[data-systems]')).forEach(b => b.classList.toggle('on', +b.dataset.cells === n)); render(); }
    }));
    $$('.bc-loadkind [data-load]').forEach(b => b.addEventListener('click', () => {
        S.loadKind = b.dataset.load;
        $$('.bc-loadkind [data-load]').forEach(x => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        $('#bc-inv').disabled = S.loadKind !== 'w';
        render();
    }));
    $$('.bc-state [data-state]').forEach(b => b.addEventListener('click', () => {
        S.state = b.dataset.state;
        $$('.bc-state [data-state]').forEach(x => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        render();
    }));
    $('#bc-unit').addEventListener('change', e => { S.unit = e.target.value; render(); });
    $('#bc-inv').addEventListener('change', e => { S.inv = e.target.checked; $('#bc-inv-fields').hidden = !S.inv; render(); });
    [['#bc-cap', 'cap'], ['#bc-par', 'par'], ['#bc-crate', 'crate'], ['#bc-cellmax', 'cellmax'], ['#bc-load', 'load'],
     ['#bc-eff', 'eff'], ['#bc-idle', 'idle'], ['#bc-from', 'from'], ['#bc-dod', 'dod'], ['#bc-duty', 'duty'],
     ['#bc-temp', 'temp'], ['#bc-soh', 'soh'], ['#bc-peukert', 'peukert'], ['#bc-volt', 'volt'],
     ['#bc-charger', 'charger'], ['#bc-ch-from', 'chFrom'], ['#bc-ch-to', 'chTo'], ['#bc-ch-temp', 'chTemp']].forEach(([s, k]) => bindNum(s, k));
    // On a phone the answer sits below the whole form; the line under the
    // fields shows it as it changes and brings the full answer into view.
    $('#bc-live').addEventListener('click', () => {
        const a = $('.bc .tc-stage .tc-panel');
        if (a) a.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        T.touch('live_jump');
    });
    $('#bc-to-runtime').addEventListener('click', () => {
        if (lastSoc != null) { S.from = Math.round(lastSoc); $('#bc-from').value = String(S.from); }
        setMode('runtime'); render();
    });
    $('#bc-to-charge').addEventListener('click', () => {
        if (S.mode === 'soc' && lastSoc != null) { S.chFrom = Math.round(lastSoc); $('#bc-ch-from').value = String(S.chFrom); }
        else if (S.mode === 'runtime') {
            const c = B.chem(S.type);
            S.chFrom = Math.round(S.dod === S.dod ? 100 - S.dod : 100 - c.dod * 100); $('#bc-ch-from').value = String(S.chFrom);
        }
        setMode('charge'); render();
    });
    $('#bc-copy').addEventListener('click', async () => {
        if (!lastText) return;
        try { await navigator.clipboard.writeText(lastText); window.__toast && window.__toast(tr('copied'), 'success'); } catch (e) { /* clipboard off */ }
    });
    $$('#bc-presets [data-preset]').forEach(b => b.addEventListener('click', () => { preset(b.dataset.preset); T.touch('preset_' + b.dataset.preset); }));
    let rz = null;
    window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(drawChart, 150); });
    setType(S.type, true);
    setMode(S.mode);
    fill();
    render();
}

// Presets: the batteries people actually ask about.
function preset(id) {
    const P = {
        car: { type: 'car', cells: 6, cap: 60, unit: 'ah', par: 1, mode: 'soc', volt: NaN, loadKind: 'w', load: 60, inv: false },
        ups: { type: 'agm', cells: 12, cap: 9, unit: 'ah', par: 1, mode: 'runtime', loadKind: 'w', load: 300, inv: true, eff: 85, idle: 10, dod: 100 },
        lfp: { type: 'lifepo4', cells: 4, cap: 100, unit: 'ah', par: 1, mode: 'runtime', loadKind: 'w', load: 500, inv: true, eff: 90, idle: 8 },
        fridge: { type: 'lifepo4', cells: 4, cap: 100, unit: 'ah', par: 1, mode: 'runtime', loadKind: 'w', load: 60, inv: false, duty: 35 },
        fpv: { type: 'lipo', cells: 4, cap: 1500, unit: 'mah', par: 1, mode: 'runtime', loadKind: 'a', load: 30, inv: false, crate: 75 },
        pack: { type: 'liion', cells: 4, cap: 3000, unit: 'mah', par: 3, mode: 'runtime', loadKind: 'a', load: 10, inv: false, cellmax: 10 },
    }[id];
    if (!P) return;
    Object.assign(S, { duty: 100, from: 100, dod: NaN, temp: 25, soh: 100, peukert: NaN }, P);
    setType(S.type, true);
    setMode(S.mode);
    fill();
    render();
}

// state → controls
function fill() {
    const set = (s, v) => { const el = $(s); if (el) el.value = (v === v && v != null) ? String(v) : ''; };
    set('#bc-cap', S.cap); $('#bc-unit').value = S.unit; set('#bc-par', S.par);
    set('#bc-crate', S.crate); set('#bc-cellmax', S.cellmax); set('#bc-load', S.load);
    $('#bc-inv').checked = !!S.inv; $('#bc-inv-fields').hidden = !S.inv; $('#bc-inv').disabled = S.loadKind !== 'w';
    set('#bc-eff', S.eff); set('#bc-idle', S.idle); set('#bc-from', S.from); set('#bc-dod', S.dod);
    set('#bc-duty', S.duty); set('#bc-temp', S.temp); set('#bc-soh', S.soh); set('#bc-peukert', S.peukert);
    set('#bc-volt', S.volt); set('#bc-charger', S.charger); set('#bc-ch-from', S.chFrom); set('#bc-ch-to', S.chTo); set('#bc-ch-temp', S.chTemp);
    $$('.bc-loadkind [data-load]').forEach(x => { const on = x.dataset.load === S.loadKind; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('.bc-state [data-state]').forEach(x => { const on = x.dataset.state === S.state; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    markCells();
}

// ── memory and telemetry ─────────────────────────────────────────────────
function save() {
    try { window.__toolSettings && window.__toolSettings.save(SLUG, 'state', JSON.stringify(S)); } catch (e) { /* storage off */ }
}
function load() {
    try {
        const raw = window.__toolSettings ? window.__toolSettings.load(SLUG, 'state', '') : '';
        if (!raw) return;
        const o = JSON.parse(raw);
        if (!o || !B.types().includes(o.type)) return;
        for (const k of Object.keys(S)) if (o[k] !== undefined && o[k] !== null) S[k] = o[k];
        if (['runtime', 'soc', 'charge'].indexOf(S.mode) < 0) S.mode = 'runtime';
    } catch (e) { /* storage off or old shape */ }
}
const T = (() => {
    let timer = null;
    const sent = new Set();
    return {
        touch(action) {
            if (sent.has(action)) return;
            sent.add(action);
            try { window.__ae && window.__ae('feature_use', { slug: SLUG, action }); } catch (e) { /* optional */ }
        },
        done() {
            clearTimeout(timer);
            timer = setTimeout(() => {
                try { window.__aeToolComplete && window.__aeToolComplete({ mode: S.mode, type: S.type, cells: S.cells }); } catch (e) { /* optional */ }
            }, 2000);
        },
    };
})();

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
